// @flow
import * as React from 'react';
import { Accordion, Box } from 'grommet';
import { FormattedMessage } from 'react-intl';
import type { RackDetailedRepresentation } from 'esm-web-pro/types/networks';
import Point12Row from 'esm-web-pro/containers/pages/network/objects/rack/Point12Row';
import { sortBy } from 'lodash';
import { point12Messages } from 'esm-web-pro/messages/points12Messages';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import ContentLabel from 'esm-web-pro/components/layout/sidePanel/ContentLabel';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import AccordionPanel from 'esm-web-pro/components/layout/sidePanel/AccordionPanel';
import PermitDocumentList from 'esm-web-pro/components/pages/network/PermitDocumentList';
import { ObjectContext } from 'esm-web-pro/containers/map/MapObject';

type Props = {
    rack: RackDetailedRepresentation,
}

const getODFName = (
    point: Object, formatMessage: Function,
) => `${formatMessage(point12Messages[point.type])} ${point.sequence}`;

const getSwitchName = (
    point: Object, formatMessage: Function,
) => point.oss_switch_name;

const RackContent = (props: Props) => {
    const { rack } = props;

    const [clickedRow, setClickedRow] = React.useState('');
    const [activeIndices, setActiveIndices] = React.useState([0]);

    const { reload } = React.useContext(ObjectContext);

    const onClick = (point: Object) => {
        setClickedRow(`point-${point.id}`);
    };

    return (
        rack ? (
            <Accordion activeIndex={activeIndices} onActive={setActiveIndices}>
                <AccordionPanel
                    label={(
                        <FormattedMessage
                            id="networks-rack-odf-switch"
                            defaultMessage="ODFs and Switches"
                        />
                    )}
                >
                    <Box height={{ min: '100%' }}>
                        <Box pad={{ bottom: 'small' }}>
                            <ContentLabel>
                                <FormattedMessage id="rack-content-odfs-title" defaultMessage="ODFs" />
                            </ContentLabel>
                            {sortBy(rack.odfs, 'sequence').map((odf) => (
                                <Point12Row
                                    point={odf}
                                    onClick={onClick}
                                    clicked={clickedRow}
                                    key={`odf-row-${odf.uuid}`}
                                    getName={getODFName}
                                />
                            ))}
                            {rack.odfs.length === 0
                    && (
                        <ContentRow padLevel={1}>
                            <ValueText>
                                <FormattedMessage id="rack-content-no-odfs" defaultMessage="No added ODFs" />
                            </ValueText>
                        </ContentRow>
                    )}
                        </Box>
                        <ContentLabel>
                            <FormattedMessage id="rack-content-switches-title" defaultMessage="Switches" />
                        </ContentLabel>
                        {sortBy(rack.switches, 'sequence').map((currSwitch) => (
                            <Point12Row
                                point={currSwitch}
                                onClick={onClick}
                                clicked={clickedRow}
                                key={`switch-row-${currSwitch.uuid}`}
                                getName={getSwitchName}
                            />
                        ))}
                        {rack.switches.length === 0
                && (
                    <ContentRow padLevel={1}>
                        <ValueText>
                            <FormattedMessage id="rack-content-no-switches" defaultMessage="No added switches" />
                        </ValueText>
                    </ContentRow>
                )}
                    </Box>
                </AccordionPanel>
                <AccordionPanel
                    label={(
                        <FormattedMessage
                            id="permit-documents"
                            defaultMessage="Permit documents"
                        />
                    )}
                >
                    <PermitDocumentList
                        documents={rack.permit_documents}
                        reload={reload}
                    />
                </AccordionPanel>
            </Accordion>
        ) : null
    );
};

export default RackContent;
